import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  noIndex?: boolean;
}

const DEFAULT_TITLE = 'ChopperSwap — The Professional Helicopter Exchange | Buy, Sell, Rent & Lease Helicopters, Rotorcraft, Turbine Engines & Parts';
const DEFAULT_DESCRIPTION = 'ChopperSwap is the global rotary-wing aircraft marketplace — buy, sell, rent, dry-lease and wet-lease helicopters (Robinson, Bell, Airbus, Leonardo, Sikorsky, MD), turbine engines, rotorcraft parts and tools, plus aviation jobs and MRO services. Helicopters, not motorcycles.';
const DEFAULT_KEYWORDS = 'helicopter marketplace, rotorcraft marketplace, rotary-wing aircraft, buy helicopter, sell helicopter, helicopter for sale, helicopter rental, dry lease helicopter, wet lease helicopter, turbine helicopter, piston helicopter, turbine engines, helicopter parts, helicopter tools, aviation jobs, helicopter pilot jobs, helicopter mechanic jobs, MRO, FAA, EASA, Robinson R44, Bell 407, Airbus H125, Leonardo AW139, Sikorsky S-92';
const DEFAULT_OG_IMAGE = 'https://www.chopperswap.com/og-card-dark-20260515-v2.png';
const SITE_URL = 'https://www.chopperswap.com';

export const SEOHead = ({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  canonicalUrl,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  noIndex = false,
}: SEOHeadProps) => {
  const fullTitle = title ? `${title} | ChopperSwap` : DEFAULT_TITLE;
  const fullCanonicalUrl = canonicalUrl ? `${SITE_URL}${canonicalUrl}` : undefined;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="ChopperSwap" />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Canonical URL */}
      {fullCanonicalUrl && <link rel="canonical" href={fullCanonicalUrl} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="ChopperSwap" />
      {fullCanonicalUrl && <meta property="og:url" content={fullCanonicalUrl} />}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content="@chopperswap" />
      
      {/* Additional SEO */}
      <meta name="theme-color" content="#1e40af" />
      <meta name="apple-mobile-web-app-title" content="ChopperSwap" />
    </Helmet>
  );
};

// Pre-configured SEO for common pages
export const seoConfigs = {
  home: {
    title: undefined, // Uses default full title
    description: 'Discover premium helicopters, engines, and parts worldwide on ChopperSwap. Buy, sell, and rent luxury aircraft with verified listings, secure document vault, and instant sharing.',
    canonicalUrl: '/',
  },
  marketplace: {
    title: 'Helicopter Marketplace - Browse Verified Listings',
    description: 'Browse hundreds of verified helicopter, engine, and parts listings. Filter by manufacturer, price, location, and more. Find your perfect aircraft today.',
    canonicalUrl: '/marketplace',
  },
  pricing: {
    title: 'Pricing Plans - List Your Aircraft from $29',
    description: 'Affordable pricing for helicopter, engine, and parts listings. FREE buy requests forever. List your aircraft for as low as $19 with our competitive pricing.',
    canonicalUrl: '/pricing',
  },
  auth: {
    title: 'Sign In or Create Account',
    description: 'Join ChopperSwap to buy, sell, or rent helicopters, engines, and parts. Create your free account and start listing today.',
    canonicalUrl: '/auth',
    noIndex: true,
  },
  profile: {
    title: 'My Profile',
    description: 'Manage your ChopperSwap profile, listings, messages, and account settings.',
    noIndex: true,
  },
  dashboard: {
    title: 'Dashboard',
    description: 'View your ChopperSwap dashboard with active listings, buy requests, and analytics.',
    noIndex: true,
  },
  messages: {
    title: 'Messages',
    description: 'View and respond to messages from buyers and sellers on ChopperSwap.',
    noIndex: true,
  },
  createListing: {
    title: 'Create New Listing',
    description: 'List your helicopter, engine, or parts for sale or rent on ChopperSwap.',
    noIndex: true,
  },
  privacy: {
    title: 'Privacy Policy',
    description: 'ChopperSwap privacy policy - Learn how we protect your data and privacy when using our helicopter marketplace.',
    canonicalUrl: '/privacy',
  },
  terms: {
    title: 'Terms of Service',
    description: 'ChopperSwap terms of service - Read our terms and conditions for using the helicopter marketplace.',
    canonicalUrl: '/terms',
  },
  cookies: {
    title: 'Cookie Policy',
    description: 'ChopperSwap cookie policy - Learn about how we use cookies to improve your experience.',
    canonicalUrl: '/cookies',
  },
  competitors: {
    title: 'Why Choose ChopperSwap - Competitor Comparison',
    description: 'See how ChopperSwap compares to other aviation marketplaces. 97% lower fees, modern features, and superior user experience.',
    canonicalUrl: '/competitors',
  },
  tutorials: {
    title: 'How-To Tutorials & Guides',
    description: 'Learn how to use ChopperSwap with our comprehensive tutorials. Create listings, manage your account, and more.',
    canonicalUrl: '/tutorials',
  },
  visitCard: {
    title: 'Digital Visit Card',
    description: 'Share your ChopperSwap listing with a beautiful digital visit card.',
    noIndex: true,
  },
  notFound: {
    title: '404 - Page Not Found',
    description: 'The page you are looking for does not exist.',
    noIndex: true,
  },
  // Dynamic company profile SEO generator
  companyProfile: (company: {
    name: string;
    description?: string;
    slug: string;
    logo?: string;
    location?: string;
  }) => ({
    title: `${company.name} - Aviation Company Profile`,
    description: company.description || `View ${company.name}'s aviation services and listings on ChopperSwap. Browse helicopters, engines, parts, jobs, and services.`,
    canonicalUrl: `/company/${company.slug}`,
    ogImage: company.logo || DEFAULT_OG_IMAGE,
    ogType: 'website' as const,
    keywords: `${company.name}, aviation company, helicopter services, ${company.location || 'worldwide'}`,
  }),
  // Dynamic user/seller profile SEO generator
  sellerProfile: (seller: {
    name: string;
    userId: string;
    listingCount?: number;
  }) => ({
    title: `${seller.name} - Seller Profile`,
    description: `View ${seller.name}'s ${seller.listingCount || ''} listings on ChopperSwap. Browse their aviation inventory and services.`,
    canonicalUrl: `/user/${seller.userId}`,
    noIndex: false,
  }),
};

export default SEOHead;
